/**
 * Client-side headline builder for /search pages.
 *
 * Mirrors the gateway's ContentMetaUtil.buildHeadline so that when /api/search
 * returns without SEO content (no contentTitle), the frontend can produce the
 * same dynamic headline the landing-page endpoint would.
 */

// Same priority & label maps as selfstorage_api_gateway ContentMetaConstants
const AMENITY_PRIORITY = [
	'climateControlled',
	'driveUp',
	'hasSpecial',
	'firstFloor',
	'alarm',
	'twentyFourHourAccess',
	'hasOnlineMoveins',
	'cameras',
	'eGateAccess',
	'getOpenNow',
	'fencedAndLit',
	'kiosk',
	'sqft',
];

const AMENITY_HEADLINE_LABEL_MAP = {
	climateControlled: 'Climate Controlled',
	driveUp: 'Drive Up',
	hasSpecial: 'Specials',
	firstFloor: 'First Floor',
	alarm: 'Alarm Secured Unit',
	twentyFourHourAccess: '24 Hour',
	hasOnlineMoveins: 'Online Move-ins',
	hasContactlessMoveins: 'Contactless Move-In',
	cameras: 'Surveillance Cameras',
	eGateAccess: 'Electronic Gate Access',
	getOpenNow: 'Currently Open',
	fencedAndLit: 'Fenced and Lit',
	kiosk: 'Self-Service Kiosk',
};

const AMENITY_LABEL_MAP = {
	climateControlled: 'Climate Controlled',
	driveUp: 'Drive-Up Access',
	firstFloor: 'First Floor',
	hasSpecial: 'Specials',
	alarm: 'Alarm Secured Unit',
	twentyFourHourAccess: '24-Hour Access',
	hasOnlineMoveins: 'Online Move-ins',
	getOpenNow: 'Currently Open',
	hasContactlessMoveins: 'Contactless Move-In',
	cameras: 'Surveillance Cameras',
	eGateAccess: 'Electronic Gate Access',
	fencedAndLit: 'Fenced and Lit',
	kiosk: 'Self-Service Kiosk',
};

const ALP_CONFIG = {
	'24-hour-access': {
		headlinePhrase: 'Cheap 24 Hour Self Storage',
	},
	'climate-controlled': {
		headlinePhrase: 'Cheap Climate Controlled Self Storage',
	},
	'drive-up-storage-units': {
		headlinePhrase: 'Cheap Drive Up Self Storage',
	},
	electricity: {
		headlinePhrase: 'Cheap Self Storage With Electricity',
	},
};

const SLP_CONFIG = {
	Mini: { headlinePhrase: 'Cheap Mini Self Storage' },
	Lockers: { headlinePhrase: 'Cheap Self Storage Lockers' },
	Small: { headlinePhrase: 'Cheap Small Self Storage Units' },
	Medium: { headlinePhrase: 'Cheap Medium Self Storage Units' },
	Large: { headlinePhrase: 'Cheap Large Self Storage Units' },
};

const VLP_CONFIG = {
	Boat: { headlinePhrase: 'Cheap Self Storage for Boats' },
	Car: { headlinePhrase: 'Cheap Self Storage for Cars' },
	RV: { headlinePhrase: 'Cheap RV Self Storage' },
};

const NMP_CONFIG = {
	'24-hour': { headlinePhrase: '24 Hour Self Storage Near Me' },
	'climate-controlled': {
		headlinePhrase: 'Climate Controlled Self Storage Near Me',
	},
	'drive-up': { headlinePhrase: 'Drive Up Self Storage Near Me' },
	electricity: { headlinePhrase: 'Self Storage With Electricity Near Me' },
	mini: { headlinePhrase: 'Mini Self Storage Near Me' },
	small: { headlinePhrase: 'Small Self Storage Units Near Me' },
	medium: { headlinePhrase: 'Medium Self Storage Units Near Me' },
	large: { headlinePhrase: 'Large Self Storage Units Near Me' },
	lockers: { headlinePhrase: 'Self Storage Lockers Near Me' },
	boat: { headlinePhrase: 'Boat Self Storage Near Me' },
	car: { headlinePhrase: 'Car Self Storage Near Me' },
	rv: { headlinePhrase: 'RV Self Storage Near Me' },
	deals: { headlinePhrase: 'Self Storage Deals Near Me' },
};

// Maps individual dimension sqft ranges to size labels
const SQFT_RANGE_TO_SIZE_LABEL = {
	'0-25': '5x5',
	'26-60': '5x10',
	'61-75': '5x15',
	'76-100': '10x10',
	'101-150': '10x15',
	'151-200': '10x20',
	'201-250': '10x25',
	'251-300': '10x30',
};

// Maps combined sqft ranges (from the new filter UI storageSizeOptions)
// directly to SLP category names.
const COMBINED_SQFT_TO_CATEGORY = {
	'0-25': 'Mini',
	'26-75': 'Small',
	'76-200': 'Medium',
	'201-999': 'Large',
	'0-5': 'Lockers',
};

// Maps dimension labels to SLP category names so that /search filter
// headlines match what the landing pages display.
const SIZE_LABEL_TO_CATEGORY = {
	'5x5': 'Mini',
	'5x10': 'Small',
	'5x15': 'Small',
	'10x10': 'Medium',
	'10x15': 'Medium',
	'10x20': 'Large',
	'10x25': 'Large',
	'10x30': 'Large',
};

function getAmenityLabel(key) {
	if (key === 'hasSpecial') return 'Specials';
	if (key === 'getOpenNow') return 'Currently Open';
	return AMENITY_LABEL_MAP[key] || key;
}

function getAmenityHeadlineLabel(key) {
	return AMENITY_HEADLINE_LABEL_MAP[key] || getAmenityLabel(key);
}

function getUnitSizeLabel(params) {
	if (params.landingPageSize) return params.landingPageSize;
	const sqft = params.sqft;
	if (!sqft || typeof sqft !== 'string') return null;
	// Check combined ranges from the new filter UI first
	if (COMBINED_SQFT_TO_CATEGORY[sqft]) return COMBINED_SQFT_TO_CATEGORY[sqft];
	// Fall back to individual dimension ranges from the old filter UI
	const dimensionLabel = SQFT_RANGE_TO_SIZE_LABEL[sqft] || sqft;
	return SIZE_LABEL_TO_CATEGORY[dimensionLabel] || dimensionLabel;
}

function getSelectedAmenities(params) {
	const list = params.amenities || [];
	return AMENITY_PRIORITY.filter(
		(key) =>
			list.includes(key) ||
			params[key] === true ||
			params[key] === 'true',
	);
}

function getVehicleFilter(params) {
	if (params.searchType === 'vehicle' || params.vehicle) {
		const vehicle = params.vehicle || 'car';
		if (vehicle.toLowerCase() === 'rv') return 'RV';
		return vehicle.charAt(0).toUpperCase() + vehicle.slice(1).toLowerCase();
	}
	return null;
}

function parseFilters(params) {
	const unitSize = getUnitSizeLabel(params);
	const amenityKeys = getSelectedAmenities(params);
	const vehicle = getVehicleFilter(params);
	const totalFilters =
		(unitSize ? 1 : 0) + amenityKeys.length + (vehicle ? 1 : 0);
	return {
		unitSize,
		amenityKeys,
		vehicle,
		totalFilters,
		hasThreeOrMoreFilters: totalFilters >= 3,
	};
}

function getActiveFilters(filters) {
	const { unitSize, amenityKeys, vehicle, hasThreeOrMoreFilters } = filters;
	if (hasThreeOrMoreFilters) {
		return {
			unitSize: unitSize || null,
			amenityKey: amenityKeys[0] || null,
			secondAmenityKey: null,
			vehicle: unitSize ? null : vehicle || null,
		};
	}
	return {
		unitSize: unitSize || null,
		amenityKey: amenityKeys[0] || null,
		secondAmenityKey: amenityKeys[1] || null,
		vehicle: vehicle || null,
	};
}

function isSingleFilter(activeFilters) {
	if (activeFilters.secondAmenityKey) return false;
	const count =
		(activeFilters.amenityKey ? 1 : 0) +
		(activeFilters.unitSize ? 1 : 0) +
		(activeFilters.vehicle ? 1 : 0);
	return count === 1;
}

function getSlpConfig(size) {
	return SLP_CONFIG[size] || { headlinePhrase: `Cheap ${size} Self Storage` };
}

function getVlpConfig(vehicle) {
	return (
		VLP_CONFIG[vehicle] || {
			headlinePhrase: `Cheap ${vehicle} Self Storage`,
		}
	);
}

function resolveSingleFilterContext(params, activeFilters) {
	if (!activeFilters || !isSingleFilter(activeFilters)) return null;
	if (params.amenity && ALP_CONFIG[params.amenity]) {
		return { config: ALP_CONFIG[params.amenity] };
	}
	if (activeFilters.unitSize) {
		const size = params.landingPageSize || activeFilters.unitSize;
		return { config: getSlpConfig(size) };
	}
	if (activeFilters.vehicle) {
		return { config: getVlpConfig(activeFilters.vehicle) };
	}
	return null;
}

function buildHeadlineLead(activeFilters) {
	const { unitSize, amenityKey, secondAmenityKey, vehicle } = activeFilters;
	const a1 = amenityKey ? getAmenityHeadlineLabel(amenityKey) : null;
	const a2 = secondAmenityKey
		? getAmenityHeadlineLabel(secondAmenityKey)
		: null;
	if (amenityKey && secondAmenityKey) return `${a1} ${a2}`;
	if (amenityKey && unitSize) return `${a1} ${unitSize}`;
	if (amenityKey && vehicle) return `${a1} ${vehicle}`;
	if (unitSize && vehicle) return `${unitSize} ${vehicle}`;
	return null;
}

function hasOnlineMoveinsFilter(activeFilters) {
	return (
		activeFilters.amenityKey === 'hasOnlineMoveins' ||
		activeFilters.secondAmenityKey === 'hasOnlineMoveins'
	);
}

function getPrimaryAmenityExcludingOnlineMoveins(activeFilters) {
	return activeFilters.amenityKey === 'hasOnlineMoveins'
		? activeFilters.secondAmenityKey
		: activeFilters.amenityKey;
}

function buildOnlineMoveinsPrefix(activeFilters) {
	const { unitSize, vehicle } = activeFilters;
	const primaryAmenity =
		getPrimaryAmenityExcludingOnlineMoveins(activeFilters);

	if (unitSize && primaryAmenity) {
		return `${unitSize} ${getAmenityHeadlineLabel(primaryAmenity)}`;
	}
	if (unitSize) return unitSize;
	if (primaryAmenity && vehicle) {
		return `${getAmenityHeadlineLabel(primaryAmenity)} ${vehicle}`;
	}
	if (primaryAmenity) return getAmenityHeadlineLabel(primaryAmenity);
	if (vehicle) return vehicle;
	return '';
}

/**
 * Builds a dynamic headline from search params and location text.
 * Mirrors selfstorage_api_gateway ContentMetaUtil.buildHeadline exactly.
 *
 * @param {Object} searchParams - The current search query params
 * @param {string} locationText - e.g. "Austin, TX"
 * @returns {string} The headline, e.g. "Cheap Climate Controlled Self Storage in Austin, TX"
 */
export default function buildHeadline(searchParams, locationText) {
	if (!searchParams) return `Cheap Self Storage in ${locationText}`;

	if (
		searchParams.pageType === 'nearMeSearchPage' &&
		searchParams.subPageType
	) {
		const nmpConfig = NMP_CONFIG[searchParams.subPageType];
		if (nmpConfig) return nmpConfig.headlinePhrase;
	}

	const filters = parseFilters(searchParams);

	if (filters.totalFilters === 0) {
		return `Cheap Self Storage in ${locationText}`;
	}

	const activeFilters = getActiveFilters(filters);
	const { amenityKey } = activeFilters;

	if (hasOnlineMoveinsFilter(activeFilters)) {
		const prefix = buildOnlineMoveinsPrefix(activeFilters);
		const contentPrefix = prefix ? `${prefix} ` : 'Cheap ';
		return `${contentPrefix}Self Storage with Online Move-ins in ${locationText}`;
	}

	const ctx = resolveSingleFilterContext(searchParams, activeFilters);
	if (ctx) {
		return `${ctx.config.headlinePhrase} in ${locationText}`;
	}

	if (amenityKey && isSingleFilter(activeFilters)) {
		const label = getAmenityHeadlineLabel(amenityKey);
		return `Cheap ${label} Self Storage in ${locationText}`;
	}

	const lead = buildHeadlineLead(activeFilters);
	if (lead) {
		return `${lead} Self Storage in ${locationText}`;
	}

	return `Cheap Self Storage in ${locationText}`;
}
